import type { ReactNode } from 'react';
import {
  BarChart3, CalendarDays, ClipboardList, Columns3, Eye, FileSpreadsheet, FolderKanban, Gauge, Headphones, History, Kanban,
  CreditCard, LayoutDashboard, ListTodo, Mic, ListChecks, Megaphone, MessagesSquare, MessageSquareText, Rocket, Settings, Shirt, Trophy, Users, Video, Wallet, Clock, Inbox, Sparkles, Gamepad2,
} from 'lucide-react';
import type { Role } from '@/lib/types';

export interface NavItem {
  to: string; label: string; icon: ReactNode; end?: boolean; mobile?: boolean; short?: string;
  /** A count on the item; `urgent` makes it the pulsing red dot. */
  badge?: number; urgent?: boolean;
}
export interface NavContext {
  projectId?: number | null; reviews?: number; unread?: number; techManager?: boolean; games?: number;
  /** How many live projects a salesperson is on; their project pages show only when they are on one. */
  projects?: number;
  /** Can make payment links (admins, sales, project leads); `payDecide` = their links waiting on "keep or close?". */
  payments?: boolean; payDecide?: number;
  /** Runs the appointment setters' outreach (the closer); `outreachDue` = things waiting on the setter. */
  outreach?: boolean; outreachDue?: number;
  /** Has a text kit (leads picked for them to text from Zoom); `textsToSend` = still to text. */
  texts?: boolean; textsToSend?: number;
  /** A salesperson an admin made a sales lead: they get the Team page. */
  salesLead?: boolean;
}
export interface NavGroup { label: string; items: NavItem[] }

const i = (C: typeof Gauge) => <C className="size-[18px]" strokeWidth={2.1} />;

export function navFor(role: Role, ctx: NavContext = {}): NavGroup[] {
  const groups = baseNav(role, ctx);
  // A salesperson put on a project (e.g. as its account manager) works it like the production team.
  if (role === 'sales' && ctx.projects) {
    groups.splice(1, 0, {
      label: 'Projects',
      items: [
        { to: '/projects', label: 'Projects', icon: i(FolderKanban), badge: ctx.unread },
        { to: '/work/tasks', label: 'My tasks', short: 'Tasks', icon: i(ListTodo) },
        { to: '/work/calendar', label: 'Project calendar', short: 'Calendar', icon: i(CalendarDays) },
      ],
    });
  }
  // Project managers (production staff who lead a project) make payment links too.
  if (role === 'production' && ctx.payments) {
    groups[0].items.push({ to: '/payments', label: 'Payments', icon: i(CreditCard), badge: ctx.payDecide });
  }
  // Technical managers (any staff role) get their client meetings.
  // The closer (a salesperson) follows the setters' outreach.
  if (role === 'sales' && ctx.outreach) {
    groups[0].items.push({ to: '/outreach', label: 'Outreach', icon: i(Mic) });
  }
  if ((role === 'sales' || role === 'admin' || role === 'setter') && ctx.texts) {
    const sales = groups[0].items;
    const at = role === 'sales' ? sales.findIndex((x) => x.to === '/sales/meetings') : sales.length;
    // Setters work from a phone, so Texts sits in their bottom bar.
    sales.splice(at, 0, { to: '/sales/texts', label: 'Texts', icon: i(MessageSquareText), badge: ctx.textsToSend, mobile: role === 'setter' });
  }
  if (role === 'sales' && ctx.salesLead) {
    groups[0].items.push({ to: '/team', label: 'Team', icon: i(Users) });
  }
  if (ctx.techManager && role !== 'client' && role !== 'setter') {
    groups[0].items.push({ to: '/meetings/tech', label: 'Client meetings', short: 'Meetings', icon: i(Video) });
  }
  // Break-time games for everyone on the team.
  if (role !== 'client' && role !== 'setter') {
    groups[groups.length - 1].items.push({ to: '/games', label: 'Mini Games', short: 'Games', icon: i(Gamepad2), badge: ctx.games });
  }
  return groups;
}

function baseNav(role: Role, ctx: NavContext): NavGroup[] {
  const me: NavGroup = {
    label: 'You',
    items: [
      { to: '/me/agent', label: 'My agent', icon: i(Shirt) },
      ...(role !== 'client' && role !== 'setter' ? [{ to: '/me/pay', label: 'Pay & attendance', icon: i(Wallet) }] : []),
    ],
  };
  switch (role) {
    case 'sales':
      return [
        {
          label: 'Sales',
          items: [
            { to: '/sales', label: 'Dialer', icon: i(Headphones), end: true, mobile: true },
            { to: '/sales/leads', label: 'My leads', short: 'Leads', icon: i(Inbox), mobile: true },
            { to: '/sales/pipeline', label: 'Pipeline', icon: i(Kanban), mobile: true },
            { to: '/sales/meetings', label: 'Meetings', icon: i(CalendarDays) },
            { to: '/payments', label: 'Payments', icon: i(CreditCard), badge: ctx.payDecide },
            { to: '/sales/log', label: 'Call log', icon: i(History) },
            { to: '/sales/analytics', label: 'Analytics', short: 'Stats', icon: i(BarChart3), mobile: true },
            { to: '/leaderboard', label: 'Leaderboard', icon: i(Trophy) },
          ],
        },
        me,
      ];
    case 'admin':
      return [
        {
          label: 'Company',
          items: [
            { to: '/admin', label: 'Overview', icon: i(LayoutDashboard), end: true, mobile: true },
            { to: '/admin/sales', label: 'Sales floor', short: 'Sales', icon: i(Gauge), mobile: true },
            { to: '/team', label: 'Calls & texts', icon: i(Users) },
            { to: '/admin/meetings', label: 'Meetings calendar', short: 'Meetings', icon: i(CalendarDays) },
            { to: '/projects', label: 'Projects', icon: i(FolderKanban), mobile: true, badge: ctx.unread },
            { to: '/admin/leads', label: 'Leads & import', short: 'Leads', icon: i(FileSpreadsheet) },
            { to: '/payments', label: 'Payments', icon: i(CreditCard), badge: ctx.payDecide },
            { to: '/outreach', label: 'Outreach', icon: i(Mic) },
            { to: '/leaderboard', label: 'Leaderboard', icon: i(Trophy) },
          ],
        },
        {
          label: 'People',
          items: [
            { to: '/admin/people', label: 'Team & access', short: 'Team', icon: i(Users), mobile: true },
            { to: '/admin/attendance', label: 'Attendance', icon: i(Clock) },
            { to: '/admin/payroll', label: 'Payroll', icon: i(Wallet) },
            { to: '/admin/audit', label: 'Activity log', icon: i(ClipboardList) },
            { to: '/admin/settings', label: 'Rules & settings', icon: i(Settings) },
          ],
        },
        { label: 'You', items: [{ to: '/me/agent', label: 'My agent', icon: i(Shirt) }] },
      ];
    case 'setter':
      return [
        {
          label: 'Outreach',
          items: [
            { to: '/outreach', label: 'Today', icon: i(Mic), end: true, mobile: true, badge: ctx.outreachDue },
            { to: '/outreach/list', label: 'My prospects', short: 'Prospects', icon: i(ListChecks), mobile: true },
          ],
        },
        me,
      ];
    case 'production':
      return [
        {
          label: 'Production',
          items: [
            { to: '/work', label: 'My workspace', short: 'Home', icon: i(Sparkles), end: true, mobile: true },
            { to: '/projects', label: 'Projects', icon: i(FolderKanban), mobile: true, badge: ctx.unread },
            { to: '/work/tasks', label: 'My tasks', short: 'Tasks', icon: i(ListTodo), mobile: true },
            { to: '/work/calendar', label: 'Calendar', icon: i(CalendarDays), mobile: true },
          ],
        },
        me,
      ];
    default: {
      const base = ctx.projectId ? `/projects/${ctx.projectId}` : '/portal';
      return [
        {
          label: 'Your project',
          items: [
            { to: base, label: 'Overview', icon: i(LayoutDashboard), end: true, mobile: true },
            { to: `${base}/files`, label: 'Reviews & files', short: 'Reviews', icon: i(Eye), mobile: true, badge: ctx.reviews, urgent: true },
            { to: `${base}/board`, label: 'Progress board', short: 'Board', icon: i(Columns3), mobile: true },
            { to: `${base}/messages`, label: 'Messages', icon: i(MessagesSquare), mobile: true, badge: ctx.unread },
            { to: `${base}/calendar`, label: 'Calendar', icon: i(CalendarDays) },
            { to: `${base}/content`, label: 'Content calendar', icon: i(Megaphone) },
            { to: `${base}/sprints`, label: 'Sprints', icon: i(Rocket) },
            { to: `${base}/team`, label: 'Your team', icon: i(Users) },
          ],
        },
        me,
      ];
    }
  }
}
